import React, { useState, useEffect } from "react";
import axios from "axios";

const ShowDataMaharastra = () => {
  // Service persons state
  const [servicePersons, setServicePersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [isPersonOpen, setIsPersonOpen] = useState(false);

  // Installations state (server-side pagination + search)
  const [installationData, setInstallationData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  // Client-side status filter (applies after data is fetched)
  const [filterStatus, setFilterStatus] = useState("all");

  // Server-side search term (debounced)
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Approval state
  const [approvingId, setApprovingId] = useState(null);
  const [expandedInstallation, setExpandedInstallation] = useState(null);

  // State & pagination (server-side)
  const [selectedState, setSelectedState] = useState(null);
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);
  const itemsPerPage = 10;

  const states = [
    "Maharashtra",
    "Haryana",
    "Punjab",
    "Rajasthan",
    "Chhattisgarh",
  ];

  // Debounce search: wait 500ms before applying
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch service persons when state changes
  useEffect(() => {
    if (selectedState) {
      fetchServicePersons(selectedState);
    }
  }, [selectedState]);

  // Reset page when selected person changes OR when debounced search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPerson, debouncedSearch]);

  // Fetch installations when selected person, page, or debounced search changes
  useEffect(() => {
    if (selectedPerson) {
      fetchInstallationData(selectedPerson._id, currentPage, debouncedSearch);
    }
  }, [selectedPerson, currentPage, debouncedSearch]);

  // ---------- API calls ----------
  const fetchServicePersons = async (state) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/state-wise-service-persons?state=${state}`
      );
      if (response.data.success) {
        setServicePersons(response.data.data);
      } else {
        throw new Error(response.data.message || "Failed to fetch service persons");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchInstallationData = async (installerId, page, search = "") => {
    setDataLoading(true);
    setDataError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/show-new-install-data`,
        {
          params: {
            installerId,
            page,
            limit: itemsPerPage,
            s: search, // 👈 server-side search parameter
          },
        }
      );

      if (response.data.success) {
        setInstallationData(response.data.data);
        const pages = response.data.totalPages || response.data.total_pages;
        setTotalPages(pages || null);
      } else {
        throw new Error(response.data.message || "Failed to fetch installation data");
      }
    } catch (err) {
      setDataError(err.message);
    } finally {
      setDataLoading(false);
    }
  };

  const handleApprove = async (installation) => {
    if (!window.confirm("Are you sure you want to approve this installation?")) {
      return;
    }

    setApprovingId(installation._id);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/update-incoming-item-status`,
        {
          installationId: installation._id,
          farmerSaralId: installation.farmerDetails?.saralId || "",
          empId: selectedPerson._id,
          accepted: true,
        }
      );

      if (response.data.success) {
        // Update local state to reflect approval
        setInstallationData((prevData) =>
          prevData.map((item) =>
            item._id === installation._id ? { ...item, installationDone: true } : item
          )
        );
        alert("Installation approved successfully!");
      } else {
        throw new Error(response.data.message || "Failed to approve installation");
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setApprovingId(null);
    }
  };

  // ---------- UI handlers ----------
  const toggleStateDropdown = () => setIsStateOpen(!isStateOpen);
  const togglePersonDropdown = () => {
    if (selectedState) setIsPersonOpen(!isPersonOpen);
  };

  const handleSelectState = (state) => {
    setSelectedState(state);
    setIsStateOpen(false);
    setSelectedPerson(null);
    setCurrentPage(1);
    setTotalPages(null);
    setSearchTerm(""); // reset search when state changes
    setDebouncedSearch("");
  };

  const handleSelectPerson = (person) => {
    setSelectedPerson(person);
    setIsPersonOpen(false);
    setExpandedInstallation(null);
    setCurrentPage(1);
    setTotalPages(null);
    setSearchTerm(""); // reset search when person changes
    setDebouncedSearch("");
  };

  const toggleExpandInstallation = (id) => {
    setExpandedInstallation(expandedInstallation === id ? null : id);
  };

  const goToPage = (page) => {
    if (page < 1) return;
    if (totalPages !== null && page > totalPages) return;
    if (page > currentPage && installationData.length < itemsPerPage) return;
    setCurrentPage(page);
  };

  // Client-side status filtering (applies only to current page data)
  const filteredInstallations = installationData.filter((installation) => {
    if (filterStatus === "all") return true;
    if (filterStatus === "completed") return installation.installationDone;
    if (filterStatus === "pending") return !installation.installationDone;
    return true;
  });

  const hasNextPage = () => {
    if (totalPages !== null) return currentPage < totalPages;
    return installationData.length === itemsPerPage;
  };

  const hasPrevPage = () => currentPage > 1;

  // ---------- Helper components ----------
  const DropdownArrow = ({ isOpen }) => (
    <span
      className={`ml-2 inline-block w-0 h-0 border-4 border-transparent ${
        isOpen
          ? "border-b-0 border-t-gray-600 border-l-4 border-r-4"
          : "border-t-0 border-b-gray-600 border-l-4 border-r-4"
      }`}
    />
  );

  const renderItemDetail = (label, value) => (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500">{label}:</span>
      <span className="text-sm font-medium">{value || "N/A"}</span>
    </div>
  );

  const formatDate = (dateString) => {
    if (!dateString || dateString === "Invalid Date") return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ---------- Main render ----------
  if (error) {
    return <div className="text-center py-8 text-red-600">Error: {error}</div>;
  }

  return (
    <div className="mx-auto p-4">
      <div className="text-xl font-bold mb-4">Installation Approval Data</div>

      {/* Dropdowns row */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        {/* State dropdown */}
        <div className="relative w-full md:w-64">
          <div
            className="flex justify-between items-center w-full p-2 border border-gray-300 rounded bg-white cursor-pointer"
            onClick={toggleStateDropdown}
          >
            <span>{selectedState || "Select state"}</span>
            <DropdownArrow isOpen={isStateOpen} />
          </div>
          {isStateOpen && (
            <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 max-h-60 overflow-y-auto">
              {states.map((state) => (
                <div
                  key={state}
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelectState(state)}
                >
                  {state}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Service person dropdown */}
        {selectedState && (
          <div className="relative w-full md:w-64">
            <div
              className="flex justify-between items-center w-full p-2 border border-gray-300 rounded bg-white cursor-pointer"
              onClick={togglePersonDropdown}
            >
              <span>
                {selectedPerson ? selectedPerson.name : "Select service person"}
              </span>
              <DropdownArrow isOpen={isPersonOpen} />
            </div>
            {isPersonOpen && (
              <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 max-h-60 overflow-y-auto">
                {servicePersons.map((person) => (
                  <div
                    key={person._id}
                    className="p-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleSelectPerson(person)}
                  >
                    {person.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Server-side search input + client-side status filter */}
        {selectedPerson && (
          <div className="flex flex-col sm:flex-row gap-2 w-full md:flex-1">
            <input
              type="text"
              placeholder="Search by farmer name, saral ID, village, block, pump/controller number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="p-2 border border-gray-300 rounded flex-grow"
            />
            {/* <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="p-2 border border-gray-300 rounded w-full sm:w-auto"
            >
              <option value="all">All Installations</option>
              <option value="completed">Completed Only</option>
              <option value="pending">Pending Only</option>
            </select> */}
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="overflow-auto max-h-[calc(100vh-200px)]">
        {dataLoading && (
          <div className="text-center py-4">Loading installations...</div>
        )}
        {dataError && (
          <div className="text-center py-4 text-red-600">Error: {dataError}</div>
        )}

        {!selectedState ? (
          <div className="text-center text-gray-500 py-8">
            Please select a state to view service persons
          </div>
        ) : !selectedPerson ? (
          <div className="text-center text-gray-500 py-8">
            {loading
              ? "Loading service persons..."
              : servicePersons.length > 0
              ? "Please select a service person"
              : "No service persons found for this state"}
          </div>
        ) : filteredInstallations.length > 0 ? (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                Installations for {selectedPerson.name}
                <span className="text-sm font-normal text-gray-500 ml-2">
                  (Page {currentPage})
                </span>
                {searchTerm && (
                  <span className="text-sm font-normal text-blue-600 ml-2">
                    Search: "{searchTerm}"
                  </span>
                )}
              </h2>
            </div>

            <div className="space-y-4">
              {filteredInstallations.map((installation) => (
                <div
                  key={installation._id}
                  className={`border border-gray-200 rounded overflow-hidden ${
                    expandedInstallation === installation._id ? "shadow-lg" : ""
                  }`}
                >
                  {/* Card header (always visible) */}
                  <div
                    className="p-4 bg-gray-50 cursor-pointer"
                    onClick={() => toggleExpandInstallation(installation._id)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <h3 className="text-lg font-semibold flex flex-wrap items-center gap-2">
                        {installation.farmerDetails?.saralId ||
                          installation.farmerSaralId ||
                          "N/A"}
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            installation.installationDone
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {installation.installationDone ? "Completed" : "Pending"}
                        </span>
                      </h3>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(installation);
                        }}
                        className={`px-3 py-1 rounded text-sm ${
                          installation.installationDone || approvingId === installation._id
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-yellow-500 text-dark"
                        }`}
                        disabled={approvingId === installation._id || installation.installationDone}
                      >
                        {approvingId === installation._id
                          ? "Approving..."
                          : installation.installationDone
                          ? "Already Approved"
                          : "Approve Installation"}
                      </button>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex gap-4">
                      <span>Sent on: {formatDate(installation.sendingDate)}</span>
                      {installation.updatedAt && (
                        <span>Last updated: {formatDate(installation.updatedAt)}</span>
                      )}
                    </div>
                  </div>

                  {/* Expanded details */}
                  {expandedInstallation === installation._id && (
                    <div className="p-4 border-t border-gray-200">
                      {/* System Information */}
                      <div className="mb-4">
                        <h4 className="font-medium mb-2">System Information</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          {renderItemDetail("System", installation.systemId?.systemName)}
                          {renderItemDetail("Warehouse", installation.warehouseId?.warehouseName)}
                          {renderItemDetail("Pump Number", installation.pumpNumber)}
                          {renderItemDetail("Motor Number", installation.motorNumber)}
                          {renderItemDetail("Controller Number", installation.controllerNumber)}
                          {renderItemDetail("RMU Number", installation.rmuNumber)}
                          {renderItemDetail(
                            "Installation Status",
                            installation.installationDone ? "Completed" : "Pending"
                          )}
                        </div>
                      </div>

                      {/* Farmer Details */}
                      <div className="mb-4">
                        <h4 className="font-medium mb-2">Farmer Details</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          {renderItemDetail("Contact", installation.farmerDetails?.contact)}
                          {renderItemDetail("Village", installation.farmerDetails?.village)}
                          {renderItemDetail("Block", installation.farmerDetails?.block)}
                          {renderItemDetail("District", installation.farmerDetails?.district)}
                          {renderItemDetail("State", installation.farmerDetails?.state)}
                          {renderItemDetail("HP", installation.farmerDetails?.HP)}
                          {renderItemDetail("Type", installation.farmerDetails?.AC_DC)}
                          {renderItemDetail("Pump Type", installation.farmerDetails?.pump_type)}
                          {renderItemDetail("Latitude", installation.farmerDetails?.latitude)}
                          {renderItemDetail("Longitude", installation.farmerDetails?.longitude)}
                        </div>
                      </div>

                      {/* Panel Numbers */}
                      <div className="mb-4">
                        <h4 className="font-medium mb-2">
                          Panel Numbers ({installation.panelNumbers?.length || 0})
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {installation.panelNumbers?.length > 0 ? (
                            installation.panelNumbers.map((panel, index) => (
                              <span key={index} className="px-2 py-1 bg-gray-100 rounded text-xs">
                                {panel}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">No panel numbers available</span>
                          )}
                        </div>
                      </div>

                      {/* Extra Panel Numbers */}
                      {installation.extraPanelNumbers?.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-medium mb-2">
                            Extra Panel Numbers ({installation.extraPanelNumbers.length})
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {installation.extraPanelNumbers.map((panel, index) => (
                              <span key={index} className="px-2 py-1 bg-gray-100 rounded text-xs">
                                {panel}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Items List */}
                      <div className="mb-4">
                        <h4 className="font-medium mb-2">
                          Items List ({installation.itemsList?.length || 0})
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {installation.itemsList?.length > 0 ? (
                            installation.itemsList.map((item, index) => (
                              <div key={index} className="border border-gray-200 rounded p-2">
                                <div className="font-medium text-sm">
                                  {item.systemItemId?.itemName || "Item"}
                                </div>
                                <div className="text-xs text-gray-600">
                                  <span>Qty: {item.quantity}</span>
                                  {item.systemItemId?.itemCode && (
                                    <span className="ml-2">Code: {item.systemItemId.itemCode}</span>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm text-gray-500">No items listed</div>
                          )}
                        </div>
                      </div>

                      {/* Extra Items List */}
                      {installation.extraItemsList?.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-medium mb-2">
                            Extra Items List ({installation.extraItemsList.length})
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {installation.extraItemsList.map((item, index) => (
                              <div key={index} className="border border-gray-200 rounded p-2">
                                <div className="font-medium text-sm">
                                  {item.systemItemId?.itemName ||
                                    (typeof item.systemItemId === "string"
                                      ? "Item ID: " + item.systemItemId
                                      : "Item")}
                                </div>
                                <div className="text-xs text-gray-600">
                                  <span>Qty: {item.quantity}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination controls */}
            {(hasPrevPage() || hasNextPage()) && (
              <div className="flex justify-center mt-6">
                <nav className="flex items-center gap-1">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={!hasPrevPage()}
                    className={`px-3 py-1 rounded border ${
                      !hasPrevPage()
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Previous
                  </button>

                  {totalPages !== null && totalPages > 1 && (
                    <>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`px-3 py-1 rounded border ${
                            page === currentPage
                              ? "bg-blue-500 text-white border-blue-500"
                              : "bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </>
                  )}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={!hasNextPage()}
                    className={`px-3 py-1 rounded border ${
                      !hasNextPage()
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </>
        ) : (
          <div className="text-center text-gray-500 py-8">
            No installations found for {selectedPerson.name}
            {searchTerm && ` matching “${searchTerm}”`}
            {filterStatus !== "all" && ` with status ${filterStatus}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShowDataMaharastra;